/**
 * useJournalEntries — data-fetching, filtering, and sort state for the
 * Journal Entries list page.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'

import {
  buildApiFilters,
  fetchAllEntries,
  getInitialConditions,
} from './useJournalEntries-helpers'
import { useToast } from '../../components/ui'
import { useOrganization } from '../../stores/organization-store'
import { useSettings } from '../../stores/settings-store'

import type { DisplayEntry } from './journal-entries-helpers'
import type { JournalListView } from './list-view-persistence'
import type { FilterBarCondition } from '@elsapiens/ui'

const PAGE_SIZE = 50

// Re-export helpers for tests/backwards compatibility
export {
  buildApiFilters,
  sortEntries,
  mapLocalEntry,
  filterLocalEntries,
  fetchAllEntries,
} from './useJournalEntries-helpers'

// ── Hook return type ──────────────────────────────────────────────────────────

export interface JournalEntriesState {
  entries: DisplayEntry[]
  sortedEntries: DisplayEntry[]
  isLoading: boolean
  isLoadingMore: boolean
  hasNextPage: boolean
  totalCount: number | undefined
  searchQuery: string
  setSearchQuery: (q: string) => void
  conditions: FilterBarCondition[]
  setConditions: (c: FilterBarCondition[]) => void
  activeTab: string
  setActiveTab: (t: string) => void
  sortField: string
  setSortField: (f: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (d: 'asc' | 'desc') => void
  currencyView: 'transaction' | 'base'
  setCurrencyView: (v: 'transaction' | 'base') => void
  /** Current 1-based page number. */
  currentPage: number
  /** Total number of pages (>= 1), derived from totalCount/pageSize. */
  totalPages: number
  /** Items per page. */
  pageSize: number
  fetchEntries: () => Promise<void>
  /** Navigate directly to a page (offset-based; replaces the current page's rows). */
  goToPage: (page: number) => Promise<void>
}

// ── Hook ──────────────────────────────────────────────────────────────────────

interface JournalEntriesStateBag {
  entries: DisplayEntry[]
  setEntries: React.Dispatch<React.SetStateAction<DisplayEntry[]>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  isLoadingMore: boolean
  setIsLoadingMore: React.Dispatch<React.SetStateAction<boolean>>
  nextPageToken: string
  setNextPageToken: React.Dispatch<React.SetStateAction<string>>
  hasNextPage: boolean
  setHasNextPage: React.Dispatch<React.SetStateAction<boolean>>
  totalCount: number | undefined
  setTotalCount: React.Dispatch<React.SetStateAction<number | undefined>>
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
  conditions: FilterBarCondition[]
  setConditions: React.Dispatch<React.SetStateAction<FilterBarCondition[]>>
  sortField: string
  setSortField: React.Dispatch<React.SetStateAction<string>>
  sortDirection: 'asc' | 'desc'
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>
  currencyView: 'transaction' | 'base'
  setCurrencyView: React.Dispatch<React.SetStateAction<'transaction' | 'base'>>
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
}

function useJournalState(initialTab: string, initialView?: JournalListView | null): JournalEntriesStateBag {
  const [entries, setEntries] = useState<DisplayEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [nextPageToken, setNextPageToken] = useState('')
  const [hasNextPage, setHasNextPage] = useState(false)
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState(initialView?.searchQuery ?? '')
  const [activeTab, setActiveTab] = useState(initialView?.activeTab ?? initialTab)
  const [conditions, setConditions] = useState<FilterBarCondition[]>(
    () => initialView?.conditions ?? getInitialConditions(initialTab),
  )
  const [sortField, setSortField] = useState(initialView?.sortField ?? 'entryDate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialView?.sortDirection ?? 'desc')
  const [currencyView, setCurrencyView] = useState<'transaction' | 'base'>('transaction')
  const [currentPage, setCurrentPage] = useState(initialView?.currentPage ?? 1)
  return {
    entries, setEntries, isLoading, setIsLoading, isLoadingMore, setIsLoadingMore,
    nextPageToken, setNextPageToken, hasNextPage, setHasNextPage, totalCount, setTotalCount,
    searchQuery, setSearchQuery, activeTab, setActiveTab, conditions, setConditions,
    sortField, setSortField, sortDirection, setSortDirection, currencyView, setCurrencyView,
    currentPage, setCurrentPage,
  }
}

interface FetchersDeps {
  organizationId: string
  apiFilters: ReturnType<typeof buildApiFilters>
  baseCurrency: string
  addToast: ReturnType<typeof useToast>['addToast']
}

function useFetchEntries(s: JournalEntriesStateBag, deps: FetchersDeps): () => Promise<void> {
  const { setEntries, setIsLoading, setNextPageToken, setHasNextPage, setTotalCount, setCurrentPage } = s
  const { organizationId, apiFilters, baseCurrency, addToast } = deps
  return useCallback(async () => {
    setIsLoading(true)
    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500))
    const result = await fetchAllEntries({
      organizationId, apiFilters, baseCurrency,
      minLoadingTime, pageSize: PAGE_SIZE,
    })
    if (result.error) { addToast({ variant: 'error', message: result.error }) }
    setEntries(result.entries)
    setNextPageToken(result.pageInfo.nextPageToken)
    setHasNextPage(result.pageInfo.hasNextPage)
    setTotalCount(result.pageInfo.totalCount)
    setCurrentPage(1)
    setIsLoading(false)
  }, [organizationId, apiFilters, baseCurrency, addToast,
      setEntries, setIsLoading, setNextPageToken, setHasNextPage, setTotalCount, setCurrentPage])
}

// Offset-based page navigation: the journal API's pageToken is the numeric row
// offset, so page P maps directly to offset (P-1)*PAGE_SIZE. Each page replaces
// the current rows (true paging, not infinite-scroll accumulation).
function useGoToPage(s: JournalEntriesStateBag, deps: FetchersDeps): (page: number) => Promise<void> {
  const { setEntries, setIsLoadingMore, setNextPageToken, setHasNextPage, setTotalCount, setCurrentPage, isLoading } = s
  const { organizationId, apiFilters, baseCurrency, addToast } = deps
  return useCallback(async (page: number) => {
    const target = Math.max(1, Math.floor(page))
    if (isLoading) { return }
    setIsLoadingMore(true)
    const result = await fetchAllEntries({
      organizationId, apiFilters, baseCurrency,
      minLoadingTime: Promise.resolve(), pageSize: PAGE_SIZE,
      pageToken: target > 1 ? String((target - 1) * PAGE_SIZE) : '',
    })
    if (result.error) {
      addToast({ variant: 'error', message: result.error })
      setIsLoadingMore(false)
      return
    }
    setEntries(result.entries)
    setNextPageToken(result.pageInfo.nextPageToken)
    setHasNextPage(result.pageInfo.hasNextPage)
    setTotalCount(result.pageInfo.totalCount)
    setCurrentPage(target)
    setIsLoadingMore(false)
  }, [isLoading, organizationId, apiFilters, baseCurrency, addToast,
      setEntries, setIsLoadingMore, setNextPageToken, setHasNextPage, setTotalCount, setCurrentPage])
}

function useEntryFetchers(
  s: JournalEntriesStateBag,
  organizationId: string,
  apiFilters: ReturnType<typeof buildApiFilters>,
  baseCurrency: string,
  addToast: ReturnType<typeof useToast>['addToast'],
): { fetchEntries: () => Promise<void>; goToPage: (page: number) => Promise<void> } {
  const deps: FetchersDeps = { organizationId, apiFilters, baseCurrency, addToast }
  const fetchEntries = useFetchEntries(s, deps)
  const goToPage = useGoToPage(s, deps)
  return { fetchEntries, goToPage }
}

export function useJournalEntries(initialTab = 'all', initialView?: JournalListView | null): JournalEntriesState {
  const { addToast } = useToast()
  const { organizationId } = useOrganization()
  const { settings } = useSettings()
  const s = useJournalState(initialTab, initialView)
  // Sort is server-side: it is part of the API filters, so a sort change
  // produces a new fetchEntries and the effect below refetches page 1.
  const apiFilters = useMemo(
    () => ({
      ...buildApiFilters(s.searchQuery, s.conditions),
      sortField: s.sortField,
      sortDirection: s.sortDirection,
    }),
    [s.searchQuery, s.conditions, s.sortField, s.sortDirection],
  )
  const { fetchEntries, goToPage } =
    useEntryFetchers(s, organizationId, apiFilters, settings.baseCurrency, addToast)
  useEffect(() => { void fetchEntries() }, [fetchEntries])

  const pagesFromCursor = s.hasNextPage ? s.currentPage + 1 : s.currentPage
  const totalPages = s.totalCount !== undefined
    ? Math.max(1, Math.ceil(s.totalCount / PAGE_SIZE))
    : pagesFromCursor

  return {
    // Entries arrive already sorted by the server.
    entries: s.entries, sortedEntries: s.entries, isLoading: s.isLoading, isLoadingMore: s.isLoadingMore,
    hasNextPage: s.hasNextPage, totalCount: s.totalCount,
    searchQuery: s.searchQuery, setSearchQuery: s.setSearchQuery,
    conditions: s.conditions, setConditions: s.setConditions,
    activeTab: s.activeTab, setActiveTab: s.setActiveTab,
    sortField: s.sortField, setSortField: s.setSortField,
    sortDirection: s.sortDirection, setSortDirection: s.setSortDirection,
    currencyView: s.currencyView, setCurrencyView: s.setCurrencyView,
    currentPage: s.currentPage, totalPages, pageSize: PAGE_SIZE,
    fetchEntries, goToPage,
  }
}
